import React, { useState, useEffect, useMemo, useRef } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Modal, CustomSelect, toast, Button } from "../..";
import { DenmarkPublish as DenmarkPublishInterface } from "@/database";
import { updateModal } from "@/store/app/app.slice";
import DenmarkEditContent, {
  DenmarkEditContentState,
} from "./DenmarkEditContent";
import { DENMARK_STATUS, statusOptions } from "@/constants";
import { RootState, useAppDispatch } from "@/store";
import {
  getDenmarkTaxonomyList,
  getDenmarkSectionList,
  getDenmarkChildSectionList,
  fetchPriorityList,
  getAddedInList,
  getFavoriteDenmarkSectionList,
  getGlobalFavSectionList,
  markFavorite,
  removeFavorite,
  fetchHostSection,
  updatePriorityList,
  searchArticles,
  getDenmarkEntityList,
} from "@/store/media/media.action";
import {
  upsertSectionInPriorityList,
  setActivePrioritySectionId,
  updateSectionItemsInPriorityList,
  removeSectionFromPriorityList,
  clearPrioritySectionsList,
  clearDenmarkSectionList,
  setDenmarkTaxonomyCodeFetch,
  setPriorityFetching,
  setDenmarkSectionList,
} from "@/store/media/media.slice";
import { refetchAndSync, isSectionAlreadySelected } from "./priorityUtils";
import { createHostOptions } from "@/utils";
import type { DenmarkEditSectionSelectStubFn } from "@/database/media/denmarkEditSectionSelectStubApi";

interface DenmarkEditModalOwnProps {
  isOpen: boolean;
  onClose: () => void;
  publishData: DenmarkPublishInterface;
  handleDenmarkchange?: (section: any, actionMeta: any) => void;
  handleHostChangeFromSelect?: (host: any) => void;
  selectedHost?: string;
  disabled?: boolean;
  currentSelectedSection?: any[];
  selectedSections?: any[];
  selectedChip?: number;
  handlecancelOrDelete?: (id: number, type: string) => void;
  editedSections?: any[];
  onSaveAndPublish?: (updatedSections: any[]) => void;
  openPriorityPanel?: boolean;
  /** When true, only show priority panel for reorder & delete — no media injection */
  priorityOnlyMode?: boolean;
  /** Section ID of the child multi-publish target to scroll into view on open */
  scrollToMultiSection?: number;
  /** Stub wait after picking a new section from the tree in Denmark publish edit (see DenmarkPublishDetails). */
  sectionTreeSelectStub?: DenmarkEditSectionSelectStubFn;
}

const mapState = (state: RootState) => ({
  hosts: state.media.hostList.list,
  hostSubsection: state.media.hostSubSection.list,
  currentMediaDetailsForEdit: state.media.mediaDetails.media as any,
  denmarkTaxonomyList: state.media.denmarkTaxonomyList.list,
  denmarkTaxonomyListLoading: state.media.denmarkTaxonomyList.isLoading,
  singleCurrentUser: state.users.singleCurrentUser,
  denmarkSectionList: state.media.denmarkSectionList.list,
  isSectionLoading: state.media.denmarkSectionList.isLoading,
  priorityList: state.media.priorityList.list,
  prioritySectionsList: state.media.prioritySectionsList.list,
  activePrioritySectionId: state.media.prioritySectionsList.activeSectionId,
  isFetchingSection: state.media.prioritySectionsList.isFetchingSection,
  allHostsList: state.constants.allHostsList.list,
  denmarkHosts:
    state.constants.allHostsList.list &&
    Array.isArray((state.constants.allHostsList.list as any)?.hosts)
      ? ((state.constants.allHostsList.list as any)?.hosts as any[])
      : Array.isArray(state.constants.allHostsList.list)
        ? (state.constants.allHostsList.list as any[])
        : [],
  favoriteSectionList: state.media.FavoriteDenmarkSectionList.list,
  isFavLoading: state.media.FavoriteDenmarkSectionList.isLoading,
  globalFavSectionList: state.media.GlobalFavSectionList.list,
  addedInList: state.media.AddedInList.list,
  isAddedInLoading: state.media.AddedInList.isLoading,
  denmarkEntityList: state.media.denmarkEntityList.list,
  isDenmarkEntityListLoading: state.media.denmarkEntityList.isLoading,
});

const mapDispatch = {
  getDenmarkTaxonomyList,
  getDenmarkSectionList,
  getDenmarkChildSectionList,
  fetchPriorityList,
  getAddedInList,
  getFavoriteDenmarkSectionList,
  getGlobalFavSectionList,
  markFavorite,
  removeFavorite,
  fetchHostSection,
  upsertSectionInPriorityList,
  setActivePrioritySectionId,
  updateSectionItemsInPriorityList,
  removeSectionFromPriorityList,
  clearPrioritySectionsList,
  setPriorityFetching,
  setDenmarkSectionList,
  updatePriorityList,
  searchArticles,
  getDenmarkEntityList,
};

const connector = connect(mapState, mapDispatch);
type ConnectedModalProps = ConnectedProps<typeof connector>;
type Props = DenmarkEditModalOwnProps & ConnectedModalProps;

/**
 * New picks stored as child rows in `currentSelectedSection` (with `_parentSectionId`)
 * while the published parent lives in `selectedSections`. Fold those children into the
 * parent's `multi_publish_targets` for edit UI (same shape as useDenmarkPublish).
 */
function mergeDraftChildrenIntoParentMultiPublishTargets(
  combined: DenmarkPublishInterface[],
  currentSelectedSection: any[]
): DenmarkPublishInterface[] {
  const children = (currentSelectedSection || []).filter(
    (item: any) =>
      item?.job_type === "denmark" && item?._parentSectionId != null
  );
  if (children.length === 0) return combined;

  return combined.map((item) => {
    if (item.job_type !== "denmark") return item;
    const parentSectionId = Number(item.section);
    const myChildren = children.filter(
      (c: any) => Number(c._parentSectionId) === parentSectionId
    );
    if (myChildren.length === 0) return item;

    const bySection = new Map<number, any>();
    for (const t of item.multi_publish_targets || []) {
      bySection.set(Number((t as any).section), t);
    }
    for (const c of myChildren) {
      const sectionNum = Number(c.section);
      if (bySection.has(sectionNum)) continue;
      bySection.set(sectionNum, {
        section: c.section,
        parent_id: c.section,
        channel_name: c.channel_name ?? "",
        host: c.host,
        folder_path: c.folder_path ?? "",
        open_story_at: "PUBLISH_SECTION",
        open_newly_in_browser: false,
        isOverRideLinkRequired: true,
        contentPublishType: "MULTIPUBLISH",
      });
    }
    return {
      ...item,
      multi_publish_targets: Array.from(bySection.values()),
    };
  });
}

const PRIORITY_UPDATE_UNAUTHORIZED_SNIPPET = "User is not Authorized";
const PRIORITY_UPDATE_UNAUTHORIZED_TOAST =
  "You do not have access to update PL for this section";

function priorityUpdateErrorText(error: unknown): string {
  if (error == null) return "";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return "";
}

function isPriorityUpdateUnauthorized(error: unknown): boolean {
  return priorityUpdateErrorText(error).includes(
    PRIORITY_UPDATE_UNAUTHORIZED_SNIPPET
  );
}

const DenmarkEditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  publishData,
  handleDenmarkchange,
  handleHostChangeFromSelect,
  selectedHost,
  disabled,
  currentSelectedSection = [],
  selectedSections = [],
  selectedChip,
  handlecancelOrDelete,
  editedSections,
  onSaveAndPublish,
  openPriorityPanel,
  priorityOnlyMode = false,
  scrollToMultiSection,
  sectionTreeSelectStub,
  // Redux state from connector
  hosts,
  hostSubsection,
  currentMediaDetailsForEdit,
  denmarkTaxonomyList,
  denmarkTaxonomyListLoading,
  denmarkSectionList,
  isSectionLoading,
  priorityList,
  prioritySectionsList,
  activePrioritySectionId,
  isFetchingSection,
  allHostsList,
  denmarkHosts,
  favoriteSectionList,
  isFavLoading,
  globalFavSectionList,
  addedInList,
  isAddedInLoading,
  // Redux dispatch from connector
  getDenmarkTaxonomyList: dispatchGetDenmarkTaxonomyList,
  getDenmarkSectionList: dispatchGetDenmarkSectionList,
  getDenmarkChildSectionList: dispatchGetDenmarkChildSectionList,
  fetchPriorityList: dispatchFetchPriorityList,
  getAddedInList: dispatchGetAddedInList,
  getFavoriteDenmarkSectionList: dispatchGetFavoriteDenmarkSectionList,
  getGlobalFavSectionList: dispatchGetGlobalFavSectionList,
  markFavorite: dispatchMarkFavorite,
  removeFavorite: dispatchRemoveFavorite,
  fetchHostSection: dispatchFetchHostSection,
  upsertSectionInPriorityList: dispatchUpsertSectionInPriorityList,
  setActivePrioritySectionId: dispatchSetActivePrioritySectionId,
  updateSectionItemsInPriorityList: dispatchUpdateSectionItemsInPriorityList,
  removeSectionFromPriorityList: dispatchRemoveSectionFromPriorityList,
  clearPrioritySectionsList: dispatchClearPrioritySectionsList,
  setPriorityFetching: dispatchSetPriorityFetching,
  setDenmarkSectionList: dispatchSetDenmarkSectionList,
  updatePriorityList: dispatchUpdatePriorityList,
  searchArticles: dispatchSearchArticles,
  getDenmarkEntityList: dispatchGetDenmarkEntityList,
  denmarkEntityList,
  isDenmarkEntityListLoading,
  singleCurrentUser,
}) => {
  // Raw dispatch is still needed for refetchAndSync utility which expects AppDispatch
  const dispatch = useAppDispatch();

  /** Host id for section RPC: parent `selectedHost` wins; avoid String(undefined) === "undefined". */
  const resolvedSelectedHost = useMemo(() => {
    const normalize = (v: unknown): string | undefined => {
      if (v == null || v === "") return undefined;
      const s = String(v).trim();
      if (s === "undefined" || s === "NaN") return undefined;
      return s;
    };
    return normalize(selectedHost) ?? normalize(publishData?.host);
  }, [selectedHost, publishData?.host]);

  const hostOptions = createHostOptions(hosts, denmarkHosts);

  const customFetchHostSection = (params: any) => {
    dispatchFetchHostSection({ ...params, hostid: resolvedSelectedHost });
  };

  const [state, setState] = useState<DenmarkEditContentState>({
    selectedArticle: [],
    title:
      publishData?.title ||
      currentMediaDetailsForEdit?.title ||
      currentMediaDetailsForEdit?.mediaTitle ||
      "",
    titleForUrl:
      publishData?.title_for_url ||
      currentMediaDetailsForEdit?.title_for_url ||
      currentMediaDetailsForEdit?.seoPath ||
      currentMediaDetailsForEdit?.seopath ||
      "",
    description:
      publishData?.desc ||
      currentMediaDetailsForEdit?.desc ||
      currentMediaDetailsForEdit?.description ||
      "",
    seotags: publishData?.tags || [],
    selectedFolder: null,
    status: publishData?.privacy || DENMARK_STATUS.ACTIVE,
    schedule_type: "DYNAMIC",
    privacy:
      publishData?.privacy ||
      publishData?.activationStatus ||
      DENMARK_STATUS.ACTIVE,
  });

  const [showPriorityPanel, setShowPriorityPanel] = useState(
    priorityOnlyMode || (openPriorityPanel ?? false)
  );
  const [priorityReviewMode, setPriorityReviewMode] = useState(
    priorityOnlyMode ? false : false
  );
  const [showPriorityPreview, setShowPriorityPreview] = useState(false);
  const [savedSectionIds, setSavedSectionIds] = useState<Set<number>>(
    new Set()
  );
  const [isSavingPriority, setIsSavingPriority] = useState(false);
  const [taxonomyBranchConflict, setTaxonomyBranchConflict] = useState(false);
  const [previewSaveStatuses, setPreviewSaveStatuses] = useState<
    Record<number, "idle" | "loading" | "success" | "error">
  >({});
  const [prioritySaveFooterError, setPrioritySaveFooterError] =
    useState<string>("");

  const selectedChipId = priorityOnlyMode
    ? ""
    : (publishData?.published_id ??
      publishData?.section ??
      (publishData as unknown as { id?: number })?.id);
  const selectedChipTitle = priorityOnlyMode
    ? ""
    : (publishData?.channel_name ?? "");

  useEffect(() => {
    if (isOpen && resolvedSelectedHost) {
      dispatchGetGlobalFavSectionList({ host: Number(resolvedSelectedHost) });
    }
  }, [isOpen, resolvedSelectedHost]);

  useEffect(() => {
    if (isOpen) {
      setShowPriorityPanel(priorityOnlyMode || (openPriorityPanel ?? false));
      setPriorityReviewMode(priorityOnlyMode ? false : false);
      setShowPriorityPreview(false);
      setSavedSectionIds(new Set());
      setPreviewSaveStatuses({});
    }
  }, [isOpen, openPriorityPanel, priorityOnlyMode]);

  const handlePrioritySave = async () => {
    const entriesToSave = prioritySectionsList;

    if (isSavingPriority || entriesToSave.length === 0) return;
    setIsSavingPriority(true);
    setPrioritySaveFooterError("");

    const setStatus = (
      sectionId: number,
      status: "idle" | "loading" | "success" | "error"
    ) => {
      setPreviewSaveStatuses((prev) => ({ ...prev, [sectionId]: status }));
    };

    const saveResults = await Promise.all(
      entriesToSave.map(async (entry) => {
        // Skip sections that have no priority list — nothing to save.
        if (entry.priorityList?.hasPriority !== true) {
          // setStatus(entry.sectionId, "success");
          // setSavedSectionIds((prev) => new Set([...prev, entry.sectionId]));
          return true;
        }

        setStatus(entry.sectionId, "loading");
        try {
          const rawEntity =
            entry.priorityList?.priorityList?.priorityListEntity;
          const host = rawEntity?.holder?.hostId ?? 0;
          const deletedList = entry.deletedList ?? [];
          const deletedMsids = new Set(deletedList.map((d: any) => d.msid));
          const priorityListEntity =
            deletedMsids.size > 0 && rawEntity
              ? {
                  ...rawEntity,
                  priorityList: (rawEntity.priorityList ?? []).filter(
                    (item: any) => !deletedMsids.has(item.msid)
                  ),
                }
              : rawEntity;

          const response = await dispatchUpdatePriorityList({
            host: Number(host),
            email: "",
            priorityListEntity,
            deletedList,
            newlyAddedList: entry.newlyAddedList ?? [],
            sectionId: entry.sectionId,
          });

          if (response?.status === 200 && !response?.error) {
            setStatus(entry.sectionId, "success");
            setSavedSectionIds((prev) => new Set([...prev, entry.sectionId]));
            return true;
          } else {
            setStatus(entry.sectionId, "error");
            const failMsg = isPriorityUpdateUnauthorized(response?.error)
              ? PRIORITY_UPDATE_UNAUTHORIZED_TOAST
              : `Failed to save priority for: ${entry.sectionName}`;
            setPrioritySaveFooterError(failMsg);
            refetchAndSync(
              dispatch,
              entry,
              selectedChipId ?? "",
              selectedChipTitle
            );
            return false;
          }
        } catch (err) {
          setStatus(entry.sectionId, "error");
          const failMsg = isPriorityUpdateUnauthorized(err)
            ? PRIORITY_UPDATE_UNAUTHORIZED_TOAST
            : `Error saving priority for: ${entry.sectionName}`;
          setPrioritySaveFooterError(failMsg);
          refetchAndSync(
            dispatch,
            entry,
            selectedChipId ?? "",
            selectedChipTitle
          );
          return false;
        }
      })
    );

    if (saveResults.length > 0 && saveResults.every(Boolean)) {
      toast.success("Priority set successfully");
    }

    setIsSavingPriority(false);
  };

  const validateContentRef = useRef<(() => boolean) | null>(null);

  const [activeChip, setActiveChip] = useState<number | null>(null);
  useEffect(() => {
    if (selectedChip) {
      setActiveChip(selectedChip);
    }
  }, [selectedChip]);

  // Sync selectedArticle from both currentSelectedSection and selectedSections, deduplicated by section id.
  // editedSections takes priority — add them first so their updated values win over stale entries.
  useEffect(() => {
    if (!isOpen) return;
    const seen = new Set<string | number>();
    const combined: DenmarkPublishInterface[] = [];
    const addUnique = (list: any[]) => {
      (list || []).forEach((item: any) => {
        const id = item.section ?? item.id;
        if (id != null && !seen.has(id)) {
          seen.add(id);
          combined.push(item);
        }
      });
    };
    addUnique(editedSections || []);
    // Draft multi-publish children live on the parent as `multi_publish_targets`; skip duplicate rows.
    addUnique(
      (currentSelectedSection || []).filter(
        (item: any) => !item?._parentSectionId
      )
    );
    addUnique(selectedSections || []);

    const selectedArticle = mergeDraftChildrenIntoParentMultiPublishTargets(
      combined,
      currentSelectedSection || []
    );

    setState((prev) => ({
      ...prev,
      selectedArticle,
    }));
  }, [isOpen, currentSelectedSection, selectedSections, editedSections]);

  // useEffect(() => {
  //   if (!isOpen) {
  //     setActiveChip(null);
  //     return;
  //   }
  //   const articles = state.selectedArticle;
  //   if (articles.length === 0) {
  //     return;
  //   }

  //   const raw =
  //     selectedChip != null && !Number.isNaN(Number(selectedChip))
  //       ? Number(selectedChip)
  //       : null;

  //   if (
  //     raw != null &&
  //     articles.some((item: any) => Number(item?.section) === raw)
  //   ) {
  //     setActiveChip(raw);
  //     return;
  //   }

  //   const first = articles[0];
  //   const firstId = first?.section ?? first?.id;
  //   setActiveChip(firstId != null ? Number(firstId) : null);
  // }, [isOpen, selectedChip, state.selectedArticle]);

  const handleDenmarkChange = (section: any, actionMeta: any) => {
    setState((prev) => {
      if (!section) return { ...prev, selectedArticle: [] };
      if (
        isSectionAlreadySelected(
          section,
          selectedSections || [],
          prev.selectedArticle,
          activeChip
        )
      )
        return prev;
      const sectionWithHost = {
        ...section,
        host: section.host ?? publishData?.host ?? resolvedSelectedHost,
      };
      return {
        ...prev,
        selectedArticle: [...prev.selectedArticle, sectionWithHost],
      };
    });
    handleDenmarkchange?.(section, actionMeta);
  };

  const hasPublishedDenmark =
    (selectedSections || []).some((item: any) => item.job_type === "denmark") ||
    currentMediaDetailsForEdit?.socials?.some(
      (s: any) => s.job_type === "denmark"
    );

  useEffect(() => {
    if (!isOpen) setTaxonomyBranchConflict(false);
  }, [isOpen]);

  const clearDenmarkModalReduxSideEffects = () => {
    dispatchClearPrioritySectionsList();
    dispatch(clearDenmarkSectionList());
    dispatch(
      setDenmarkTaxonomyCodeFetch({
        data: { list: [] },
        isLoading: false,
        error: "",
      })
    );
  };

  const handleSaveAndPublish = async () => {
    // if (validateContentRef.current && !validateContentRef.current()) {
    //   toast.fail("Please fill in all required fields before saving.");
    //   return;
    // }
    if (taxonomyBranchConflict) {
      toast.fail(
        "Added Tags should not be from the same branch, remove any one of them"
      );
      return;
    }
    if (onSaveAndPublish) {
      // Always send only the active chip's data, never the full list.
      const activeItem = state.selectedArticle.find(
        (item: any) =>
          item?.section != null && Number(item.section) === Number(activeChip)
      );
      const payload = activeItem ? [activeItem] : state.selectedArticle;

      if (!hasPublishedDenmark) {
        // For a first-time publish, cancel all non-active chips that were
        // speculatively added so they don't linger in state.
        state.selectedArticle.forEach((item: any) => {
          if (
            Number(item?.section) !== Number(activeChip) &&
            handlecancelOrDelete
          ) {
            handlecancelOrDelete(item.section, "cancel");
          }
        });
      }

      try {
        await onSaveAndPublish(payload);
      } catch {
        // The callback already showed a toast — keep the modal open so the
        // user's edits are not lost.
        return;
      }
    }
    clearDenmarkModalReduxSideEffects();
    onClose();
  };

  const currentArticleByChip =
    state.selectedArticle.find(
      (item: any) =>
        item?.section != null && Number(item.section) === Number(activeChip)
    ) ?? state.selectedArticle[0];
  const displayStatus = String(
    currentArticleByChip?.privacy ??
      publishData?.privacy ??
      publishData?.activationStatus ??
      DENMARK_STATUS.ACTIVE
  ).toUpperCase();

  const updateSelectedArticleByChip = (updates: Record<string, any>) => {
    if (activeChip == null) return;
    setState((prev) => ({
      ...prev,
      selectedArticle: prev.selectedArticle.map((item: any) =>
        Number(item?.section) === Number(activeChip)
          ? { ...item, ...updates }
          : item
      ),
    }));
  };

  const renderStatusOption = (option: any) => (
    <div className="d-flex align-items-center gap-2">
      <i
        className={`bi ${option.icon}`}
        style={{ color: option.color, fontSize: "1.1rem" }}
      ></i>
      <span className="fw-semibold" style={{ color: option.color }}>
        {option.label}
      </span>
    </div>
  );

  const previewEntries = prioritySectionsList;
  const allPreviewDone =
    showPriorityPreview &&
    previewEntries.length > 0 &&
    previewEntries.every(
      (e) =>
        previewSaveStatuses[e.sectionId] === "success" ||
        previewSaveStatuses[e.sectionId] === "error"
    );

  /** Same rule as PriorityPanel: API must report hasPriority for the active section chip. */
  const activePrioritySectionEntry = useMemo(
    () =>
      prioritySectionsList.find(
        (e) => e.sectionId === activePrioritySectionId
      ) ?? null,
    [prioritySectionsList, activePrioritySectionId]
  );
  const hasPriorityListForActiveChip =
    activePrioritySectionEntry?.priorityList?.hasPriority === true;
  const isPrioritySaveBlocked =
    activePrioritySectionId == null || !hasPriorityListForActiveChip;

  const handleDoneClick = () => {
    clearDenmarkModalReduxSideEffects();
    onClose();
  };

  const footer = showPriorityPanel ? (
    <div className="d-flex align-items-center justify-content-between w-100">
      {showPriorityPreview ? (
        <>
          {allPreviewDone ? (
            <>
              <span
                style={{
                  fontSize: "0.82rem",
                  color: previewEntries.every(
                    (e) => previewSaveStatuses[e.sectionId] === "success"
                  )
                    ? "#16a34a"
                    : "#dc2626",
                }}
              >
                {previewEntries.every(
                  (e) => previewSaveStatuses[e.sectionId] === "success"
                )
                  ? "All sections saved successfully."
                  : prioritySaveFooterError ||
                    "Something went wrong. Please retry."}
              </span>
              <Button
                type="primary"
                HTMLButtonType="button"
                icon={<i className="bi bi-check-circle"></i>}
                onClick={handleDoneClick}
              >
                DONE
              </Button>
            </>
          ) : (
            <>
              <Button
                type="outline-secondary"
                HTMLButtonType="button"
                icon={<i className="bi bi-arrow-left"></i>}
                onClick={() => {
                  setShowPriorityPreview(false);
                  setPreviewSaveStatuses({});
                  setPrioritySaveFooterError("");
                }}
                isDisabled={isSavingPriority}
              >
                BACK
              </Button>
              <Button
                type="primary"
                HTMLButtonType="button"
                icon={
                  isSavingPriority ? undefined : (
                    <i className="bi bi-check-lg"></i>
                  )
                }
                onClick={handlePrioritySave}
                isDisabled={isSavingPriority || isPrioritySaveBlocked}
              >
                {isSavingPriority ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    SAVING...
                  </>
                ) : (
                  "SAVE"
                )}
              </Button>
            </>
          )}
        </>
      ) : (
        <>
          {prioritySaveFooterError ? (
            <span style={{ fontSize: "0.82rem", color: "#dc2626" }}>
              {prioritySaveFooterError}
            </span>
          ) : (
            <span className="text-muted d-flex align-items-center gap-2">
              <span
                className="rounded-circle flex-shrink-0"
                style={{
                  width: "10px",
                  height: "10px",
                  backgroundColor: "#f0e68c",
                }}
              />
              Your changes are highlighted in yellow.
            </span>
          )}
          {priorityReviewMode ? (
            <Button
              type="primary"
              HTMLButtonType="button"
              icon={<i className="bi bi-eye"></i>}
              onClick={() => setShowPriorityPreview(true)}
              isDisabled={isPrioritySaveBlocked}
            >
              REVIEW
            </Button>
          ) : (
            <Button
              type="primary"
              HTMLButtonType="button"
              icon={
                isSavingPriority ? undefined : (
                  <i className="bi bi-check-lg"></i>
                )
              }
              onClick={handlePrioritySave}
              isDisabled={isSavingPriority || isPrioritySaveBlocked}
            >
              {isSavingPriority ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                  SAVING...
                </>
              ) : (
                "SAVE"
              )}
            </Button>
          )}
        </>
      )}
    </div>
  ) : (
    <div className="d-flex align-items-center justify-content-end gap-3 w-100">
      {activeChip != null && (
        <div className="d-flex align-items-center gap-1">
          {/* <span className="text-uppercase">Status:</span> */}
          <div style={{ minWidth: "130px" }}>
            <CustomSelect
              name="status"
              className="w-100"
              label=""
              value={displayStatus}
              customStyling={{ border: "none", boxShadow: "none" }}
              handleChange={(option: any) => {
                const nextStatus = option?.value ?? DENMARK_STATUS.INACTIVE;
                updateSelectedArticleByChip({
                  privacy: nextStatus,
                });
              }}
              options={statusOptions}
              customOptionRenderer={renderStatusOption}
              customValueRenderer={renderStatusOption}
              placeholder="Select status"
              isMenuWidthFixed={true}
              menuWidth="130px"
              updatedOptions={statusOptions}
              isSearchable={false}
            />
          </div>
        </div>
      )}
      <Button
        type="primary"
        HTMLButtonType="button"
        icon={<i className="bi bi-check-lg"></i>}
        onClick={handleSaveAndPublish}
        isDisabled={activeChip == null}
      >
        SAVE
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        clearDenmarkModalReduxSideEffects();
        onClose();
      }}
      title={priorityOnlyMode ? "Denmark Priority" : "Denmark Publish"}
      width={
        showPriorityPanel || priorityOnlyMode || !hasPublishedDenmark
          ? "75vw"
          : "52.5vw"
      }
      height="90vh"
      updateModal={updateModal}
      closeOnOutsideClick={false}
      footer={footer}
    >
      <DenmarkEditContent
        hasPublishedDenmark={hasPublishedDenmark}
        publishData={publishData}
        state={state}
        setState={setState}
        handleDenmarkchange={handleDenmarkChange}
        handleHostChangeFromSelect={handleHostChangeFromSelect}
        hostOptions={hostOptions}
        selectedHost={resolvedSelectedHost}
        currentMediaDetailsForEdit={currentMediaDetailsForEdit}
        customFetchHostSection={customFetchHostSection}
        disabled={disabled}
        hosts={hosts}
        hostSubsection={hostSubsection}
        denmarkHosts={denmarkHosts}
        selectedChip={activeChip}
        setActiveChip={setActiveChip}
        handlecancelOrDelete={handlecancelOrDelete}
        denmarkTaxonomyList={denmarkTaxonomyList}
        denmarkTaxonomyListLoading={denmarkTaxonomyListLoading}
        getDenmarkTaxonomyList={dispatchGetDenmarkTaxonomyList}
        getDenmarkSectionList={dispatchGetDenmarkSectionList}
        denmarkSectionList={denmarkSectionList}
        getDenmarkChildSectionList={dispatchGetDenmarkChildSectionList}
        selectedChipId={selectedChipId}
        selectedChipTitle={selectedChipTitle}
        openPriorityPanel={openPriorityPanel}
        savedSectionIds={savedSectionIds}
        showPriorityPanel={showPriorityPanel}
        setShowPriorityPanel={setShowPriorityPanel}
        fetchPriorityList={dispatchFetchPriorityList}
        priorityReviewMode={priorityReviewMode}
        onPriorityReviewModeChange={setPriorityReviewMode}
        showPriorityPreview={showPriorityPreview}
        previewSaveStatuses={previewSaveStatuses}
        priorityOnlyMode={priorityOnlyMode}
        // Redux state passed as props
        prioritySectionsList={prioritySectionsList}
        activePrioritySectionId={activePrioritySectionId}
        isFetchingSection={isFetchingSection}
        allHostsList={allHostsList}
        isSectionLoading={isSectionLoading}
        favoriteSectionList={favoriteSectionList}
        isFavLoading={isFavLoading}
        globalFavSectionList={globalFavSectionList}
        addedInList={addedInList}
        isAddedInLoading={isAddedInLoading}
        // Redux dispatch callbacks passed as props
        getAddedInList={dispatchGetAddedInList}
        getFavoriteDenmarkSectionList={dispatchGetFavoriteDenmarkSectionList}
        markFavorite={dispatchMarkFavorite}
        removeFavorite={dispatchRemoveFavorite}
        upsertSectionInPriorityList={dispatchUpsertSectionInPriorityList}
        setActivePrioritySectionId={dispatchSetActivePrioritySectionId}
        updateSectionItemsInPriorityList={
          dispatchUpdateSectionItemsInPriorityList
        }
        removeSectionFromPriorityList={dispatchRemoveSectionFromPriorityList}
        clearPrioritySectionsList={dispatchClearPrioritySectionsList}
        setPriorityFetching={dispatchSetPriorityFetching}
        setDenmarkSectionList={dispatchSetDenmarkSectionList}
        scrollToMultiSection={scrollToMultiSection}
        currentSelectedSection={currentSelectedSection}
        onTaxonomyBranchConflictChange={setTaxonomyBranchConflict}
        sectionTreeSelectStub={sectionTreeSelectStub}
        allPreviewDone={allPreviewDone}
        onRegisterValidate={(fn) => {
          validateContentRef.current = fn;
        }}
        searchArticles={dispatchSearchArticles}
        getDenmarkEntityList={dispatchGetDenmarkEntityList}
        denmarkEntityList={denmarkEntityList}
        isDenmarkEntityListLoading={isDenmarkEntityListLoading}
        singleCurrentUser={singleCurrentUser}
      />
    </Modal>
  );
};

export default connector(DenmarkEditModal);
